import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, FolderOpen, FileText, BarChart3, MessageCircle,
  Settings, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Clock, Loader2, CheckCircle2, AlertCircle, Trophy, XCircle,
  Plus, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_DOT = {
  pending: 'bg-slate-400', active: 'bg-blue-500', analyzing: 'bg-[#1D4053]',
  completed: 'bg-emerald-500', quoted: 'bg-purple-500', won: 'bg-emerald-600',
  lost: 'bg-red-500', expired: 'bg-orange-500', error: 'bg-red-600',
  draft: 'bg-slate-400', sent: 'bg-blue-500', viewed: 'bg-purple-500',
  accepted: 'bg-emerald-500', rejected: 'bg-red-500', revised: 'bg-amber-500'
};

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [chatsOpen, setChatsOpen] = useState(true);
  const [expandedProjectIds, setExpandedProjectIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('sidebar_expanded_projects') || '[]')); }
    catch { return new Set(); }
  });

  // Group recent quotes by parent project for nested rendering.
  const quotesByProject = useMemo(() => {
    const m = {};
    for (const q of recentQuotes) {
      const pid = q.project_id || 'unassigned';
      (m[pid] = m[pid] || []).push(q);
    }
    return m;
  }, [recentQuotes]);

  const toggleProjectExpansion = (pid) => {
    setExpandedProjectIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      try { localStorage.setItem('sidebar_expanded_projects', JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const fetchSidebarData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sidebar/recent`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRecentProjects(data.projects || []);
        setRecentQuotes(data.quotes || []);
        setRecentChats(data.chats || []);
      }
    } catch (e) { console.error('Sidebar fetch error:', e); }
  }, []);

  useEffect(() => { fetchSidebarData(); }, [fetchSidebarData]);
  useEffect(() => {
    const interval = setInterval(fetchSidebarData, 30000);
    return () => clearInterval(interval);
  }, [fetchSidebarData]);

  const path = location.pathname;
  const isActive = (p) => path === p || (p !== '/dashboard' && path.startsWith(p));

  const fmtCurrency = (v) => {
    if (!v) return '$0';
    if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
  };

  const createNewChat = async () => {
    try {
      const res = await fetch(`${API}/chat/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: 'New Chat' })
      });
      if (res.ok) {
        const session = await res.json();
        fetchSidebarData();
        navigate(`/chat/${session.session_id}`);
      }
    } catch (e) { console.error('Create chat error:', e); }
  };

  const deleteChat = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/chat/sessions/${sessionId}`, {
        method: 'DELETE', credentials: 'include'
      });
      setRecentChats(prev => prev.filter(c => c.session_id !== sessionId));
      if (path === `/chat/${sessionId}`) navigate('/chat');
    } catch (e) { console.error('Delete chat error:', e); }
  };

  const navSections = [
    { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Chats', path: '/chat', icon: MessageCircle, isChats: true },
    { label: 'Projects', path: '/projects', icon: FolderOpen, isProjects: true },
    { label: 'Reports', path: '/reports', icon: BarChart3 },
  ];

  return (
    <aside
      data-testid="sidebar"
      className={`${collapsed ? 'w-[60px]' : 'w-[260px]'} h-screen bg-white border-r border-[#0D1830]/10 flex flex-col transition-all duration-300 ease-out flex-shrink-0 relative`}
    >
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-[#0D1830]/10 ${collapsed ? 'justify-center px-2' : 'px-5'}`}>
        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" data-testid="sidebar-logo">
          {collapsed ? (
            <div className="w-8 h-8 rounded-lg bg-[#0D1830] flex items-center justify-center text-white font-black text-sm">N</div>
          ) : (
            <>
              <div className="w-8 h-8 rounded-lg bg-[#0D1830] flex items-center justify-center text-white font-black text-sm shrink-0">N</div>
              <span className="text-lg font-extrabold text-[#0D1830] tracking-tight">Nivela</span>
            </>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="sidebar-nav">
        {navSections.map((item) => {
          const { isProjects, isChats } = item;
          const hasDropdown = isProjects || isChats;
          const active = item.path ? isActive(item.path) : false;
          const Icon = item.icon;
          const dropdownOpen = isProjects ? projectsOpen : isChats ? chatsOpen : false;

          return (
            <div key={item.label}>
              <button
                data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
                onClick={() => {
                  if (isProjects) {
                    if (collapsed) { navigate('/projects'); return; }
                    if (!projectsOpen) setProjectsOpen(true);
                    navigate('/projects');
                  } else if (isChats) {
                    if (collapsed) { navigate('/chat'); return; }
                    if (!chatsOpen) setChatsOpen(true);
                    navigate('/chat');
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                className={`w-full flex items-center gap-3 rounded-lg transition-all duration-200 group
                  ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}
                  ${active ? 'bg-[#0D1830] text-white' : 'text-[#446E87] hover:bg-[#F7FAFB] hover:text-[#0D1830]'}
                `}
                title={collapsed ? item.label : ''}
              >
                <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? 'text-white' : ''}`} />
                {!collapsed && (
                  <>
                    <span className="text-sm font-semibold flex-1 text-left">{item.label}</span>
                    {hasDropdown && (
                      <span
                        className={`p-0.5 rounded hover:bg-[#0D1830]/5 ${active ? 'text-white/70' : 'text-[#72A4BF]'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isProjects) setProjectsOpen(!projectsOpen);
                          if (isChats) setChatsOpen(!chatsOpen);
                        }}
                      >
                        {dropdownOpen
                          ? <ChevronUp className="w-3.5 h-3.5" />
                          : <ChevronDown className="w-3.5 h-3.5" />}
                      </span>
                    )}
                  </>
                )}
              </button>

              {/* Recent Chats dropdown */}
              {isChats && !collapsed && chatsOpen && (
                <div className="mt-1 ml-3 pl-4 border-l border-[#0D1830]/8 space-y-0.5" data-testid="sidebar-recent-chats">
                  <button
                    onClick={createNewChat}
                    data-testid="sidebar-new-chat"
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[#446E87] hover:bg-[#F7FAFB] hover:text-[#0D1830] transition-colors"
                  >
                    <Plus className="w-3 h-3 shrink-0" />
                    <span className="text-xs font-semibold">New Chat</span>
                  </button>
                  {recentChats.length === 0 ? (
                    <p className="text-xs text-[#72A4BF] py-1 px-2">No conversations yet</p>
                  ) : (
                    recentChats.map((c) => (
                      <button
                        key={c.session_id}
                        data-testid={`sidebar-chat-${c.session_id}`}
                        onClick={() => navigate(`/chat/${c.session_id}`)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors group/chat
                          ${path === `/chat/${c.session_id}` ? 'bg-[#0D1830]/5 text-[#0D1830]' : 'text-[#446E87] hover:bg-[#F7FAFB] hover:text-[#0D1830]'}
                        `}
                      >
                        <MessageCircle className="w-3 h-3 shrink-0 text-[#72A4BF]" />
                        <span className="text-xs font-medium truncate flex-1">{c.title || 'New Chat'}</span>
                        <span
                          className="opacity-0 group-hover/chat:opacity-100 p-0.5 rounded hover:bg-red-50 text-[#72A4BF] hover:text-red-500 transition-all"
                          onClick={(e) => deleteChat(e, c.session_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Recent Projects dropdown — quotes nested under their parent project */}
              {isProjects && !collapsed && projectsOpen && (
                <div className="mt-1 ml-3 pl-4 border-l border-[#0D1830]/8 space-y-0.5" data-testid="sidebar-recent-projects">
                  {recentProjects.length === 0 ? (
                    <p className="text-xs text-[#72A4BF] py-2 px-2">No projects yet</p>
                  ) : (
                    recentProjects.map((p) => {
                      const projectQuotes = quotesByProject[p.project_id] || [];
                      const projectExpanded = expandedProjectIds.has(p.project_id);
                      const hasQuotes = projectQuotes.length > 0;
                      return (
                        <div key={p.project_id}>
                          <div
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors group/item
                              ${path === `/projects/${p.project_id}` ? 'bg-[#0D1830]/5 text-[#0D1830]' : 'text-[#446E87] hover:bg-[#F7FAFB] hover:text-[#0D1830]'}
                            `}
                          >
                            <button
                              data-testid={`sidebar-project-${p.project_id}`}
                              onClick={() => navigate(`/projects/${p.project_id}`)}
                              className="flex-1 flex items-center gap-2 text-left min-w-0"
                            >
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[p.status] || 'bg-slate-400'}`} />
                              <span className="text-xs font-medium truncate">{p.name}</span>
                            </button>
                            {hasQuotes && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleProjectExpansion(p.project_id); }}
                                className="p-0.5 rounded hover:bg-[#0D1830]/5 text-[#72A4BF]"
                                data-testid={`sidebar-project-toggle-${p.project_id}`}
                                title={projectExpanded ? 'Hide quotes' : 'Show quotes'}
                              >
                                {projectExpanded
                                  ? <ChevronUp className="w-3 h-3" />
                                  : <ChevronDown className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                          {hasQuotes && projectExpanded && (
                            <div className="ml-3 pl-3 border-l border-[#0D1830]/8 mt-0.5 space-y-0.5">
                              {projectQuotes.map((q) => (
                                <button
                                  key={q.quote_id}
                                  data-testid={`sidebar-quote-${q.quote_id}`}
                                  onClick={() => navigate(`/projects/${p.project_id}?tab=quotes&quote=${q.quote_id}`)}
                                  className="w-full flex items-center gap-2 px-2 py-1 rounded-md text-left text-[#446E87] hover:bg-[#F7FAFB] hover:text-[#0D1830] transition-colors"
                                >
                                  <FileText className="w-3 h-3 shrink-0 text-[#72A4BF]" />
                                  <span className="text-[11px] font-medium truncate flex-1">
                                    {q.quote_number || q.quote_name || q.customer_name || 'Quote'}
                                  </span>
                                  <span className="text-[10px] text-[#72A4BF] font-medium">{fmtCurrency(q.total_price)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <button
                    onClick={() => navigate('/projects')}
                    className="w-full text-xs font-semibold text-[#72A4BF] hover:text-[#0D1830] px-2 py-1.5 text-left transition-colors"
                    data-testid="sidebar-view-all-projects"
                  >
                    View all projects
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-[#0D1830]/10 py-3 px-2 space-y-0.5">
        <button
          onClick={() => navigate('/settings')}
          data-testid="sidebar-settings"
          className={`w-full flex items-center gap-3 rounded-lg transition-all duration-200
            ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}
            ${isActive('/settings') ? 'bg-[#0D1830] text-white' : 'text-[#446E87] hover:bg-[#F7FAFB] hover:text-[#0D1830]'}
          `}
          title={collapsed ? 'Settings' : ''}
        >
          <Settings className="w-[18px] h-[18px] shrink-0" />
          {!collapsed && <span className="text-sm font-semibold">Settings</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        data-testid="sidebar-toggle"
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 bg-white border border-[#0D1830]/10 rounded-full flex items-center justify-center text-[#446E87] hover:text-[#0D1830] hover:border-[#0D1830]/25 transition-colors shadow-sm z-10"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
      </button>
    </aside>
  );
}
